import {
  EVENT_PROPERTY_ACTION,
  EVENT_PROPERTY_DESCRIPTOR,
  EVENT_PROPERTY_UI_CONTEXT,
  EventActionType,
  EventType,
} from './events'

type UserDefinedEventProperties = {
  [EVENT_PROPERTY_ACTION]: EventActionType
  [EVENT_PROPERTY_DESCRIPTOR]?: string
  [EVENT_PROPERTY_UI_CONTEXT]?: string
  [key: string]: any
}

const STRONGLY_ENCOURAGED_PROPS = [
  EVENT_PROPERTY_UI_CONTEXT,
  EVENT_PROPERTY_DESCRIPTOR,
]

export type AnalyticsClient = object

export class AnalyticsAdapter {
  private _client
  private _displayName = 'Swashbuckle Analytics'

  public constructor(client: AnalyticsClient) {
    this._client = client
  }

  private getCommonEventProperties() {
    return {
      pathname: window.location.pathname,
    }
  }

  private warn(msg) {
    console.warn(`${this._displayName} [Warning]: ${msg}`)
  }

  private error(msg) {
    console.error(`${this._displayName} [Error]: ${msg}`)
  }

  private requiredPropsArePresent(properties: UserDefinedEventProperties) {
    return STRONGLY_ENCOURAGED_PROPS.every(
      encouragedProp => properties[encouragedProp]
    )
  }

  public track(eventName: EventType, properties: UserDefinedEventProperties) {
    // If no tracking manager is provided, do nothing
    if (!this._client) return

    // Throw warnings if props we would like, but aren't required, are missing
    if (!this.requiredPropsArePresent(properties)) {
      this.warn(
        `The "descriptor" and "ui_context" properties must be provided for Swashbuckle components to fire a tracking event. Please work with your engineering manager to add an English descriptor (i.e. the button text or the input label) and the ui_context (i.e. a snake cased value describing the section of the page the component is on).`
      )
      return
    }

    // Look up the event name in the tracking client. If it exists,
    // call it with the properties provided by the caller
    const fn = this._client[eventName]
    if (typeof fn === 'function') {
      const cep = this.getCommonEventProperties()
      fn.call(this._client, Object.assign({}, properties, cep))
    } else {
      this.error(`Cannot find a valid tracking event with name "${eventName}"`)
    }
  }
}
